import React, { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  deviceCountersQueryClient,
  deviceGroupsQueryClient,
  devicesQueryClient,
  eventValueRangeQueryClient,
  initialConfigQueryClient,
  multipleReadingsExportQueryClient,
} from '../../clients/ReactQueryClients/ReactQueryClients';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';
import {
  Device,
  DeviceGroupDto,
  DeviceReadingResponseDto,
  DeviceReadingsResponseDto,
  Port,
  ReadingsExportRequest,
  ReadingsRequestDto,
  ReadingsRequestSensorDto,
  Sensor,
  SensorType,
} from '@thingslog/repositories';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../reducers';
import { Grid, useMediaQuery, useTheme, Fab } from '@mui/material';
import {
  DeviceReadingsSelector,
  InfoDisplay,
  MobileQueryPreview,
  QuickSensorSelector,
} from './components';
import { ShowChart, TableRows, TouchApp } from '@mui/icons-material';
import { SensorUtils } from '@thingslog/utils';
import { sensorTypeOptions } from './data/sensor-type-options';
import { SensorNameSnapshotter } from './utils/SensorNameSnapshotter';
import { SensorsDataVisualizer } from './components/sensor-data-visualization/SensorsDataVisualizer';
import { ReadingsDataDisplay } from './components/sensor-data-table/ReadingsDataDisplay';
import DeviceSensorsDto from '@thingslog/repositories/src/deviceSensorsDto/DeviceSensorsDto';
import Header from '../../components/header';
import {
  SensorDataAllDevicesOption,
  SensorDataDeviceGroupOptions,
  SensorDataDeviceSensorOption,
  SensorDataDeviceSensorTypeOption,
} from './models';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

export interface SensorsDataPageProps {}

export type SensorDataView = 'INFO' | 'TABLE' | 'CHART';

export const SensorsDataPage: FC<SensorsDataPageProps> = () => {
  const { t } = useTranslation();
  const { deviceNumber } = useParams<{ deviceNumber?: string }>();
  const isDeviceSpecific = Boolean(deviceNumber);
  const companyId = useDetermineCompanyId();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedDeviceNumber, setSelectedDeviceNumber] = useState<string | null>(
    deviceNumber ?? null
  );
  const [sensorVisualizerTabValue, setSensorVisualizerTabValue] = useState<'chart' | 'map'>(
    'chart'
  );
  const [selectedReadingsTab, onSelectedReadingsTabChange] = useState<'raw' | 'export'>('raw');

  const toDateTz = useSelector((state: ReduxState) => state.period.toDateTz);
  const fromDateTz = useSelector((state: ReduxState) => state.period.fromDateTz);

  const [rawPage, setRawPage] = useState(0);
  const [rawRowsPerPage, setRawRowsPerPage] = useState(10);
  const [exportPage, setExportPage] = useState(0);
  const [exportRowsPerPage, setExportRowsPerPage] = useState(10);

  const { useDevicesData } = useMemo(() => devicesQueryClient, []);
  const { useDeviceGroupsData } = useMemo(() => deviceGroupsQueryClient, []);
  const { useDevicePortsConfigData } = useMemo(() => initialConfigQueryClient, []);
  const { useFetchDevicesReadingsMutation } = useMemo(() => deviceCountersQueryClient, []);
  const { useMultipleReadingsJSON, useMultipleReadingsExportCSV, useMultipleReadingsExportExcel } =
    useMemo(() => multipleReadingsExportQueryClient, []);
  const { useDeviceEventValueRangesData } = useMemo(() => eventValueRangeQueryClient, []);
  const sensorNameSnapshotter = useMemo(() => new SensorNameSnapshotter(), []);

  const [payload, setPayload] = useState<ReadingsRequestDto>({
    preferFlow: false,
    deviceGroupIds: [],
    deviceSensorList: [],
    deviceSensorTypeList: [],
  });
  const [exportFormat, setExportFormat] = useState<'CSV' | 'EXCEL'>('CSV');

  const isSelectionMade = useMemo(() => {
    return (
      payload.deviceGroupIds.length > 0 ||
      payload.deviceSensorList.length > 0 ||
      payload.deviceSensorTypeList.length > 0
    );
  }, [payload]);

  const [readingsExportSnapshot, setReadingsExportSnapshot] = useState<DeviceSensorsDto[]>([]);
  const [isReadingsDisplayExpanded, setIsReadingsDisplayExpanded] = useState<boolean>(false);
  const [mobileDataView, setMobileDataView] = useState<SensorDataView>('INFO');

  useEffect(() => {
    if (deviceNumber) {
      setSelectedDeviceNumber(deviceNumber);
    }
    if (!deviceNumber) {
      setSelectedDeviceNumber(null);
    }
    setPayload({
      preferFlow: false,
      deviceGroupIds: [],
      deviceSensorList: [],
      deviceSensorTypeList: [],
    });
    queryClient.invalidateQueries();
  }, [deviceNumber]);

  const allDevicesQuery = useDevicesData(companyId, 0, 9999, null, null, {
    enabled: !isDeviceSpecific,
    refetchOnWindowFocus: false,
  });

  const deviceGroupsQuery = useDeviceGroupsData(companyId);
  const devicePortsConfigQuery = useDevicePortsConfigData(selectedDeviceNumber!, {
    enabled: !!selectedDeviceNumber,
    refetchOnWindowFocus: false,
    onSuccess: (data: Record<string, Port>) => {
      if (!selectedDeviceNumber) {
        return;
      }
      Object.entries(data).forEach(([index, port]: [string, Port]) => {
        const sensorIndex = parseInt(index);
        sensorNameSnapshotter.append(selectedDeviceNumber, sensorIndex, port.sensor.name);
      });
    },
  });

  const handleExportFileDownload = (data: ArrayBuffer): void => {
    const fileName = `ReadingsExport-${exportFormat}-from-${fromDateTz.toISOString()}-to-${toDateTz.toISOString()}.zip`;
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const multipleReadingsJSONMutation = useMultipleReadingsJSON(fromDateTz, toDateTz);
  const multipleReadingsExportCSVMutation = useMultipleReadingsExportCSV({
    onSuccess: handleExportFileDownload,
  });
  const multipleReadingsExportExcelMutation = useMultipleReadingsExportExcel({
    onSuccess: handleExportFileDownload,
  });

  const eventValueRangesQuery = useDeviceEventValueRangesData(selectedDeviceNumber!, {
    enabled: !!selectedDeviceNumber,
    refetchOnWindowFocus: false,
  });

  const fetchDeviceReadingsMutation = useFetchDevicesReadingsMutation(
    fromDateTz.toISOString(),
    toDateTz.toISOString(),
    {
      onSuccess: (response: DeviceReadingsResponseDto) => {
        const deviceSensorsArray: DeviceSensorsDto[] = Object.values(
          response.devicesReadings.reduce(
            (acc: Record<string, DeviceSensorsDto>, reading: DeviceReadingResponseDto) => {
              if (!acc[reading.deviceNumber]) {
                acc[reading.deviceNumber] = {
                  deviceNumber: reading.deviceNumber,
                  sensorIndexes: [],
                };
              }
              if (!acc[reading.deviceNumber].sensorIndexes.includes(reading.sensorIndex)) {
                acc[reading.deviceNumber].sensorIndexes.push(reading.sensorIndex);
              }
              return acc;
            },
            {}
          )
        );

        setReadingsExportSnapshot(deviceSensorsArray);
        multipleReadingsJSONMutation.mutate(deviceSensorsArray);
      },
    }
  );

  const allDevicesOptions: SensorDataAllDevicesOption[] = useMemo(() => {
    if (!allDevicesQuery.data) {
      return [];
    }
    return allDevicesQuery.data.content.map((device: Device) => ({
      deviceNumber: device.number,
      deviceName: device.name,
    }));
  }, [allDevicesQuery.data]);

  const allDeviceGroupsOptions: SensorDataDeviceGroupOptions[] = useMemo(() => {
    if (!deviceGroupsQuery.data) {
      return [];
    }
    return deviceGroupsQuery.data.map((group: DeviceGroupDto) => ({
      deviceGroupId: group.deviceGroupId,
      deviceGroupName: group.deviceGroupName,
    }));
  }, [deviceGroupsQuery.data]);

  const deviceSensorTypeOptions: SensorDataDeviceSensorTypeOption[] = useMemo(() => {
    return Object.keys(sensorTypeOptions).map((sensorType: string) => ({
      sensorType: sensorType as SensorType,
    }));
  }, []);

  const selectedDeviceSensorOptions: SensorDataDeviceSensorOption[] = useMemo(() => {
    if (
      !selectedDeviceNumber ||
      devicePortsConfigQuery.isFetching ||
      !devicePortsConfigQuery.data
    ) {
      return [];
    }

    const sensors = Object.entries(devicePortsConfigQuery.data).map(
      ([index, port]: [string, Port]) => {
        return { index: index, sensor: port.sensor };
      }
    );

    return sensors
      .filter(({ sensor }: { index: string; sensor: Sensor }) => {
        const sensorType = SensorUtils({ sensor: sensor }).getSensorType();
        return sensorType === 'analog' || sensorType === 'digital';
      })
      .map(({ index, sensor }: { index: string; sensor: Sensor }) => {
        return {
          sensorIndex: parseInt(index),
          sensorName: sensor.name,
        };
      });
  }, [
    selectedDeviceNumber,
    devicePortsConfigQuery.data,
    devicePortsConfigQuery.dataUpdatedAt,
    devicePortsConfigQuery.isFetching,
  ]);

  const isSpecificSelectorBlocked = useMemo(() => {
    return fetchDeviceReadingsMutation.isLoading || devicePortsConfigQuery.isFetching;
  }, [fetchDeviceReadingsMutation.isLoading, devicePortsConfigQuery.isFetching]);

  const isGenericSelectorBlocked = useMemo(() => {
    return (
      fetchDeviceReadingsMutation.isLoading ||
      devicePortsConfigQuery.isFetching ||
      deviceGroupsQuery.isFetching ||
      allDevicesQuery.isFetching
    );
  }, [
    fetchDeviceReadingsMutation.isLoading,
    devicePortsConfigQuery.isFetching,
    deviceGroupsQuery.isFetching,
    allDevicesQuery.isFetching,
  ]);

  console.log(
    'isGenericSelectorBlocked',
    fetchDeviceReadingsMutation.isLoading,
    devicePortsConfigQuery.isFetching,
    deviceGroupsQuery.isFetching,
    allDevicesQuery.isFetching
  );

  const isChartLoading = eventValueRangesQuery.isLoading || fetchDeviceReadingsMutation.isLoading;
  const isReadingTableViewLoading =
    fetchDeviceReadingsMutation.isLoading || multipleReadingsJSONMutation.isLoading;

  const handleQuickSensorSelect = (sensorIndex: number): void => {
    if (!selectedDeviceNumber) return;

    const newPayload: ReadingsRequestDto = {
      preferFlow: false,
      deviceSensorList: [
        {
          deviceNumber: selectedDeviceNumber,
          sensorIndex: sensorIndex,
        },
      ],
      deviceGroupIds: [],
      deviceSensorTypeList: [],
    };

    setPayload(newPayload);
    fetchDeviceReadingsMutation.mutate(newPayload);
    setMobileDataView('CHART');
  };

  const handleExportClicked = (format: 'CSV' | 'EXCEL'): void => {
    switch (format) {
      case 'CSV':
        multipleReadingsExportCSVMutation.mutate({
          body: {
            deviceSensors: readingsExportSnapshot,
            fromDate: fromDateTz,
            toDate: toDateTz,
          },
        });
        break;
      case 'EXCEL':
        multipleReadingsExportExcelMutation.mutate({
          body: {
            fromDate: fromDateTz,
            toDate: toDateTz,
            deviceSensors: readingsExportSnapshot,
          },
        });
        break;
      default:
        throw new Error(`Invalid export format: ${format}`);
    }
  };

  return (
    <Header>
      <div className="flex flex-col gap-4 mx-4 h-full ">
        {(!isMobile || mobileDataView === 'INFO') && (
          <section className="flex-shrink-0">
            {isDeviceSpecific ? (
              <DeviceReadingsSelector
                variant="specificDevice"
                deviceNumber={deviceNumber!}
                payload={payload}
                onPayloadChange={setPayload}
                onSubmit={(): void => {
                  fetchDeviceReadingsMutation.mutate(payload);
                  setMobileDataView('CHART');
                }}
                sensorOptions={selectedDeviceSensorOptions}
                blockInteractions={isSpecificSelectorBlocked}
                sensorNameSnapshotter={sensorNameSnapshotter}
              />
            ) : (
              <DeviceReadingsSelector
                variant="generic"
                deviceOptions={allDevicesOptions}
                deviceGroupOptions={allDeviceGroupsOptions}
                sensorOptions={selectedDeviceSensorOptions}
                deviceSensorTypeOptions={deviceSensorTypeOptions}
                payload={payload}
                onPayloadChange={setPayload}
                selectedDeviceNumber={selectedDeviceNumber}
                onSelectedDeviceNumberChange={setSelectedDeviceNumber}
                onSubmit={(): void => {
                  fetchDeviceReadingsMutation.mutate(payload);
                  setMobileDataView('CHART');
                }}
                blockInteractions={isGenericSelectorBlocked}
                sensorNameSnapshotter={sensorNameSnapshotter}
              />
            )}
          </section>
        )}
        {!isSelectionMade && !isMobile && (
          <>
            {isDeviceSpecific && (
              <QuickSensorSelector
                options={selectedDeviceSensorOptions}
                onSelect={handleQuickSensorSelect}
              />
            )}
            {!isDeviceSpecific && (
              <InfoDisplay
                icon={<TouchApp />}
                title={t('sensors_data_no_selection_made')}
                subtitle={t('sensors_data_no_selection_subtitle')}
              />
            )}
          </>
        )}
        {isMobile && (
          // Mobile layout
          <div className="w-full h-full flex-1 min-w-0">
            {mobileDataView !== 'INFO' && (
              <MobileQueryPreview
                payload={payload}
                fromDate={fromDateTz}
                toDate={toDateTz}
                onClick={(): void => setMobileDataView('INFO')}
              />
            )}
            {!isSelectionMade && (mobileDataView === 'CHART' || mobileDataView === 'TABLE') && (
              <InfoDisplay
                icon={<TouchApp />}
                title={t('sensors_data_no_selection_made')}
                subtitle={t('sensors_data_no_selection_subtitle')}
              />
            )}
            {mobileDataView === 'CHART' && isSelectionMade && (
              <div className="w-full">
                <SensorsDataVisualizer
                  selectedTab={sensorVisualizerTabValue}
                  onSelectedTabChange={setSensorVisualizerTabValue}
                  data={fetchDeviceReadingsMutation.data?.devicesReadings ?? []}
                  eventValueRanges={eventValueRangesQuery.data?.rangeEventConfigDtoList ?? []}
                  displayEventRanges={!isMobile}
                  isLoading={isChartLoading}
                />
              </div>
            )}
            {mobileDataView === 'TABLE' && isSelectionMade && (
              <ReadingsDataDisplay
                isMobile={true}
                selectedTab={selectedReadingsTab}
                onSelectedTabChange={onSelectedReadingsTabChange}
                rawData={fetchDeviceReadingsMutation.data}
                exportData={multipleReadingsJSONMutation.data}
                rawPage={rawPage}
                onRawPageChange={setRawPage}
                rawRowsPerPage={rawRowsPerPage}
                onRawRowsPerPageChange={setRawRowsPerPage}
                exportPage={exportPage}
                onExportPageChange={setExportPage}
                exportRowsPerPage={exportRowsPerPage}
                onExportRowsPerPageChange={setExportRowsPerPage}
                isExpanded={true}
                exportFormat={exportFormat}
                onExportFormatChange={setExportFormat}
                onExportClicked={handleExportClicked}
                onToggleExpanded={(): void =>
                  setIsReadingsDisplayExpanded((prev: boolean): boolean => !prev)
                }
                isLoading={isReadingTableViewLoading}
              />
            )}
          </div>
        )}
        {!isMobile && isSelectionMade && (
          // Desktop layout
          <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Grid item xs={isReadingsDisplayExpanded ? 6 : 10} sx={{ height: '100%' }}>
              <SensorsDataVisualizer
                selectedTab={sensorVisualizerTabValue}
                onSelectedTabChange={setSensorVisualizerTabValue}
                data={fetchDeviceReadingsMutation.data?.devicesReadings ?? []}
                eventValueRanges={eventValueRangesQuery.data?.rangeEventConfigDtoList ?? []}
                displayEventRanges={!isMobile}
                isLoading={isChartLoading}
              />
            </Grid>
            <Grid item xs={isReadingsDisplayExpanded ? 6 : 2} sx={{ height: '100%' }}>
              <ReadingsDataDisplay
                isMobile={false}
                selectedTab={selectedReadingsTab}
                onSelectedTabChange={onSelectedReadingsTabChange}
                rawData={fetchDeviceReadingsMutation.data}
                exportData={multipleReadingsJSONMutation.data}
                rawPage={rawPage}
                onRawPageChange={setRawPage}
                rawRowsPerPage={rawRowsPerPage}
                onRawRowsPerPageChange={setRawRowsPerPage}
                exportPage={exportPage}
                onExportPageChange={setExportPage}
                exportRowsPerPage={exportRowsPerPage}
                onExportRowsPerPageChange={setExportRowsPerPage}
                isExpanded={isReadingsDisplayExpanded}
                exportFormat={exportFormat}
                onExportFormatChange={setExportFormat}
                onExportClicked={handleExportClicked}
                onToggleExpanded={(): void =>
                  setIsReadingsDisplayExpanded((prev: boolean): boolean => !prev)
                }
                isLoading={isReadingTableViewLoading}
              />
            </Grid>
          </Grid>
        )}
        {isMobile && (
          <Fab
            color="primary"
            aria-label={
              mobileDataView === 'CHART'
                ? t('sensors_data_view_table')
                : t('sensors_data_view_chart')
            }
            onClick={(): void => {
              if (mobileDataView === 'CHART') {
                setMobileDataView('TABLE');
              } else {
                setMobileDataView('CHART');
              }
            }}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1,
            }}
          >
            {mobileDataView === 'CHART' ? <TableRows /> : <ShowChart />}
          </Fab>
        )}
      </div>
    </Header>
  );
};
